import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    chainCreditRequestDetailUrl,
    chainEntitlementUrl,
    chainPendingCreditRequestsUrl,
    chainRequestCreditsUrl,
    chainUnlockActionUrl,
    chainUnlockListUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ChainCreditEntitlement,
    ChainCreditRequest,
    ChainUnlock,
} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";

export type UnlockChainVariables = {
    entitlement: ChainCreditEntitlement;
    entityId: number;
    /** Notification copy only — never sent. */
    supplierName: string;
};

export type RequestChainCreditsVariables = {
    entitlement: ChainCreditEntitlement;
    requestedAmount: number;
    /** Free text; omitted from the body when blank. */
    approver: string;
};

export type WithdrawChainCreditRequestVariables = {
    request: ChainCreditRequest;
};

/** The two reads every credit write can move: the balance, and the list it changed. */
function invalidateEntitlement(queryClient: ReturnType<typeof useQueryClient>, entitlement: ChainCreditEntitlement) {
    void queryClient.invalidateQueries({queryKey: [chainEntitlementUrl(entitlement.databag_id)]});
}

/**
 * `POST supplier_360/chain_entitlement/{id}/unlock/` — spends one credit (§2.14). `unlocked_by`
 * is left to the server, which defaults it to the requesting user.
 *
 * A `402` means the balance the page showed was stale (someone else spent the last credit): the
 * entitlement is refetched so the panel falls through to its "out of credits" state.
 */
export function useUnlockChainMutation() {
    const queryClient = useQueryClient();

    return useMutation<ChainUnlock, DefaultError, UnlockChainVariables>({
        mutationFn: async ({entitlement, entityId}) => {
            const {data} = await queryHttp.post<ChainUnlock>(chainUnlockActionUrl(entitlement.id), {entity: entityId});
            return data;
        },
        onSuccess: (_unlock, {entitlement, supplierName}) => {
            invalidateEntitlement(queryClient, entitlement);
            void queryClient.invalidateQueries({queryKey: [chainUnlockListUrl(entitlement.id)]});
            notificationStore.show(`Value chain unlocked for ${supplierName}.`, {
                severity: 'success',
                id: `chain-unlock-success-${entitlement.id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (error, {entitlement}) => {
            const outOfCredits = isAxiosError(error) && error.response?.status === 402;
            if (outOfCredits) invalidateEntitlement(queryClient, entitlement);
            notificationStore.show(
                outOfCredits ? 'No value chain credits left.' : 'Could not unlock the value chain. Please try again.',
                {severity: 'error', id: `chain-unlock-error-${entitlement.id}`, autoHideDuration: 4000},
            );
        },
    });
}

/**
 * `POST supplier_360/chain_entitlement/{id}/request-credits/` — files a pending request (§2.14).
 * Nothing is credited until it is approved; `requested_by` is left to the server.
 */
export function useRequestChainCreditsMutation() {
    const queryClient = useQueryClient();

    return useMutation<ChainCreditRequest, DefaultError, RequestChainCreditsVariables>({
        mutationFn: async ({entitlement, requestedAmount, approver}) => {
            const {data} = await queryHttp.post<ChainCreditRequest>(chainRequestCreditsUrl(entitlement.id), {
                requested_amount: requestedAmount,
                ...(approver ? {approver} : {}),
            });
            return data;
        },
        onSuccess: (_request, {entitlement}) => {
            void queryClient.invalidateQueries({queryKey: [chainPendingCreditRequestsUrl(entitlement.id)]});
        },
        onError: (_error, {entitlement}) => {
            notificationStore.show('Could not send the credit request. Please try again.', {
                severity: 'error',
                id: `chain-request-error-${entitlement.id}`,
                autoHideDuration: 4000,
            });
        },
    });
}

/**
 * `DELETE supplier_360/chain_credit_request/{id}/` — withdraws a pending request (§2.16). The row
 * is deleted outright: the API has no "withdrawn" status, so no trace of the request remains.
 */
export function useWithdrawChainCreditRequestMutation() {
    const queryClient = useQueryClient();

    return useMutation<void, DefaultError, WithdrawChainCreditRequestVariables>({
        mutationFn: async ({request}) => {
            await queryHttp.delete(chainCreditRequestDetailUrl(request.id));
        },
        onSuccess: (_void, {request}) => {
            void queryClient.invalidateQueries({queryKey: [chainPendingCreditRequestsUrl(request.entitlement)]});
            notificationStore.show('Credit request withdrawn.', {
                severity: 'success',
                id: `chain-request-withdraw-success-${request.id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {request}) => {
            notificationStore.show('Could not withdraw the request. Please try again.', {
                severity: 'error',
                id: `chain-request-withdraw-error-${request.id}`,
                autoHideDuration: 4000,
            });
        },
    });
}
